import {arrayMax, arrayMin, filterNonUnique, groupBy, sortCompareAlphabetically} from '@/app-core/lib/utils/array'
import {clone} from '@/app-core/lib/utils/helper'
import {floatNumber} from '@/app-core/lib/helpers'
import AnalyticsCommon from '@/app-core/mixins/analytics/analyticsCommon'
import OrderTypeEnum from '@/app-core/api/enums/OrderTypeEnum'
import ServiceTypeEnum from '@/app-core/api/enums/ServiceTypeEnum'
import StoreCommon from '@/app-core/mixins/store/storeCommon'
import OrderTimeEnum from '@/app-core/api/enums/OrderTimeEnum'

export default {
  name: 'CartCommon',

  mixins: [AnalyticsCommon, StoreCommon],

  dataStore: {
    AppCart    : 'App.Cart',
    AppPayments: 'App.Payments'
  },

  data() {
    return {
      productDetailsDialogEditMode: false
    }
  },

  computed: {
    cartUniqueStoreIds() {
      return filterNonUnique(this.AppCart?.Items?.map(item => item.StoreId) || [])
    },

    cartOrderTypeIsDelivery() {
      return this.AppCart?.OrderType === OrderTypeEnum.DELIVERY
    },
    cartOrderTypeIsCollection() {
      return this.AppCart?.OrderType === OrderTypeEnum.COLLECTION
    },

    cartStoresMinCollectionTime() {
      return arrayMin(this.activeCartStores, 'CollectionTime')
    },
    cartStoresMaxCollectionTime() {
      return arrayMax(this.activeCartStores, 'CollectionTime')
    },
    cartStoresMinMaxCollectionTime() {
      return this.cartStoresMinCollectionTime === this.cartStoresMaxCollectionTime ? this.cartStoresMinCollectionTime : `${this.cartStoresMinCollectionTime}-${this.cartStoresMaxCollectionTime}`
    },

    activeCartStores() {
      return this.AppCart.Stores.filter(store => !store.IsClosed && store.CartTotal > 0)
    },

    cartStoresMinDeliveryTime() {
      return arrayMin(this.activeCartStores, 'DeliveryTime')
    },
    cartStoresMaxDeliveryTime() {
      return arrayMax(this.activeCartStores, 'DeliveryTime')
    },
    cartStoresMinMaxDeliveryTime() {
      return this.cartStoresMinDeliveryTime === this.cartStoresMaxDeliveryTime ? this.cartStoresMinDeliveryTime : `${this.cartStoresMinDeliveryTime}-${this.cartStoresMaxDeliveryTime}`
    },

    cartStoresMinMaxTime() {
      if (this.cartOrderTypeIsDelivery) return this.cartStoresMinMaxDeliveryTime
      if (this.cartOrderTypeIsCollection) return this.cartStoresMinMaxCollectionTime

      return this.cartStoresMinMaxCollectionTime
    },

    cartStoresCount() {
      return parseInt(this.AppCart?.TotalStores || 0)
    },

    cartProductsCount() {
      return parseInt(this.AppCart?.TotalItems || 0)
    },

    cartTotal() {
      return floatNumber(this.AppCart.CartTotal || 0)
    },

    orderTotal() {
      return floatNumber(this.AppCart.OrderTotal || 0)
    },

    isMinimumOrderTotalValid() {
      if (this.AppConfig.Marketplace.MinimumOrderType === ServiceTypeEnum.GLOBAL) {
        return this.orderTotalAmountValidPayment(this.selectedPaymentMethod)
      }

      if (this.AppConfig.Marketplace.MinimumOrderType === ServiceTypeEnum.STORE) {
        return this.orderTotalAmountValidAllStores()
      }

      return false
    },
    minimumOrderTotalAmountDiff() {
      if (this.AppConfig.Marketplace.MinimumOrderType === ServiceTypeEnum.GLOBAL) {
        return this.orderMinTotalAmountDiffPayment(this.selectedPaymentMethod)
      }

      if (this.AppConfig.Marketplace.MinimumOrderType === ServiceTypeEnum.STORE) {
        return this.orderMinTotalAmountDiffAllStores()
      }

      return 0
    },

    selectedPaymentMethod: {
      get() {
        return this.AppCart?.PaymentMethod || null
      },
      set(val) {
        this.AppCart.PaymentMethod = val
        this.model.Payment = val.Code
        this.setGAPaymentInfo(val)
      }
    },

    cartProducts() {
      return this.AppCart?.Items || []
    },

    cartExtraCharges() {
      return this.AppCart?.Totals?.filter(item => item.name === 'extra_charge') || []
    },

    cartExtraChargesUnique() {
      const items = this.cartExtraCharges
      const groupedItems = Object.values(
        items.reduce((acc, item) => {
          if (!acc[item.id]) {
            // Initialize with the first item's structure, but set numeric accumulators to 0
            acc[item.id] = {
              ...item,
              quantity  : 0,
              amount_raw: 0
            }
          }

          // Sum the quantities
          acc[item.id].quantity += item.quantity

          // Sum the total amounts.
          acc[item.id].amount_raw += item.amount_raw

          // Keep fixed string format for 'amount' (rounded to 2 decimals)
          acc[item.id].amount = acc[item.id].amount_raw.toFixed(2)

          return acc
        }, {})
      )

      return groupedItems
    },

    cartCoupon() {
      const excludedIds = [this.AppWaiterService.ChargeId, this.AppDeliveryService.ChargeId, this.AppSmsService.ChargeId]
      return this.AppCart?.Totals?.find(item => item.name === 'coupon' && !excludedIds.includes(item.id)) || null
    },

    cartWaiterService() {
      return this.AppCart?.Totals?.find(item => item.name === 'extra_charge' && item.id === this.AppWaiterService.ChargeId) || null
    },

    cartDeliveryService() {
      return this.AppCart?.Totals?.find(item => item.name === 'extra_charge' && item.id === this.AppDeliveryService.ChargeId) || null
    },

    cartSmsService() {
      return this.AppCart?.Totals?.find(item => item.name === 'extra_charge' && item.id === this.AppSmsService.ChargeId) || null
    },

    cartProductsGroupedByStore() {
      const items = this.cartProducts
      const groups = groupBy(items, item => item.StoreId)
      const retVal = []

      Object.keys(groups).forEach(storeItem => {
        const store = clone(this.getStoreById(storeItem))
        store.Items = groups[storeItem].sort(sortCompareAlphabetically('Name'))
        retVal.push(store)
      })

      retVal.sort(sortCompareAlphabetically('Name'))

      return retVal
    }
  },

  methods: {
    orderTotalAmountValidPayment(payment) {
      return this.orderMinTotalAmountDiffPayment(payment) <= 0
    },
    orderMinTotalAmountDiffPayment(payment) {
      return this.paymentMinOrderTotal(payment) - this.cartTotal
    },

    orderTotalAmountValidStore(storeId) {
      return this.orderMinTotalAmountDiffStore(storeId) <= 0
    },
    orderTotalAmountValidAllStores() {
      return this.orderMinTotalAmountDiffAllStores() <= 0
    },
    orderMinTotalAmountDiffStore(storeId) {
      const store = this.getCartStoreById(storeId)
      return floatNumber(store?.MinimumOrderDiff)
    },
    orderMinTotalAmountDiffAllStores() {
      return floatNumber(this.AppCart?.MinimumOrderDiff)
    },

    paymentMinOrderTotal(payment) {
      let retVal = 0
      let minOrderTotal = 0

      if (this.AppConfig.Marketplace.MinimumOrderType === ServiceTypeEnum.GLOBAL) {
        payment = payment || this.selectedPaymentMethod || this.AppPayments.find(payment => payment.Status) || null
        minOrderTotal = floatNumber(payment.MinOrderTotal || 0)
        if (!payment) return retVal
      }

      if (this.AppConfig.Marketplace.MinimumOrderType === ServiceTypeEnum.STORE) {
        minOrderTotal = floatNumber(this.AppCart.MinimumOrderDiff)
      }

      if (minOrderTotal > retVal) retVal = minOrderTotal

      return floatNumber(retVal)
    },

    storeTime(store) {
      const config = {
        sameDay : `[ ]`,
        nextDay : `[${this.$t('Common.Date.Tomorrow')}]`,
        nextWeek: 'ddd',
        lastDay : 'ddd, DD/MM',
        lastWeek: 'DD/MM/YYYY',
        sameElse: 'DD/MM/YYYY'
      }

      if (this.AppConfig.Marketplace.ScheduledOrder.Active) {
        if (this.AppCart?.ScheduledOrder?.Type === OrderTimeEnum.NOW) {
          return this.storeDefaultTime(store, 'XS')
        } else {
          const hasDate = !!this.AppCart?.ScheduledOrder?.Date
          const hasTime = !!this.AppCart?.ScheduledOrder?.Time
          const dateStr = this.$dayjs(this.AppCart?.ScheduledOrder?.DateTime)?.calendar(this.$dayjs(), config)?.trim() || ''
          const timeStr = this.AppCart?.ScheduledOrder?.Time || ''

          return `${hasDate && dateStr ? dateStr + ',' : ''} ${hasTime ? timeStr : ''}`
        }
      }

      return this.storeDefaultTime(store, 'XS')
    }
  }
}
